import { Modal } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { useQuery } from 'react-query';
import { Column } from 'react-table';
import { KTSVG } from '../../../../../../_metronic/helpers';
import { api } from '../../../../../modules/api/api';
import { useAuth } from '../../../../../modules/auth';
import SimpleDatatable from '../../../../../modules/components/datatable/SimpleDatatable';
import Time from '../../../../../modules/components/time/Time';
import { useToast } from '../../../../../modules/components/toast/ToastContext';

type Primitive = string | number | boolean | null | undefined;

type FieldUpdateHistoryItem = {
    version?: number;
    updated_by?: string | null;
    updated_at?: string;
    value?: unknown;
};

type HistoryResponse = {
    results?: Record<string, FieldUpdateHistoryItem[]>;
};

type ContextualHistoryResponse = HistoryResponse & {
    ref_id: string;
    label?: string;
    pathLabels?: Record<string, string>;
};

type Reviewer = {
    user_id?: string;
    email?: string;
    name?: string;
};

type DiffItem = {
    type: 'added' | 'removed' | 'changed';
    path: string;
    previousValue?: unknown;
    currentValue?: unknown;
};

type HistoryTableRow = {
    field: string;
    updatedBy: string;
    updatedAt?: string;
    summary: React.ReactNode;
};

type ServicePath = {
    ref_id: string;
    paths: string[];
    label?: string;
    pathLabels?: Record<string, string>;
};

type GroupedHistoryItem = {
    key: string;
    path: string;
    fieldLabel: string;
    entries: Array<FieldUpdateHistoryItem & { path: string; diffs: DiffItem[] }>;
};

type HistoryReference = 'SITE' | 'SI_REQUEST';

const emDash = '—';

const isPrimitive = (value: unknown): value is Primitive =>
    value === null || ['string', 'number', 'boolean', 'undefined'].includes(typeof value);

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const isObjectWithId = (item: unknown) => isPlainObject(item) && item !== null && '_id' in item;

const buildPath = (basePath: string, nextPath: string) => {
    if (!basePath) return nextPath;
    if (!nextPath) return basePath;
    return `${basePath}.${nextPath}`;
};

const toTitleCase = (value: string) =>
    value
        .replace(/_/g, ' ')
        .replace(/\./g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\b\w/g, (char) => char.toUpperCase());

const removeIdOrReturnRaw = <T extends unknown>(input: T): T | Omit<T, "_id"> => {
    if (input && typeof input === 'object' && '_id' in input) {
        const { _id, ...others } = input as any;
        return others;
    }

    return input;
};

const formatPathLabel = (path: string) => {
    if (!path) return emDash;

    return toTitleCase(path.replace(/^result\./, ''));
};

const formatLeafLabel = (path: string, pathPrefix = '') => {
    const normalizedPath = path.replace(/\[\d+\]/g, '');
    const lastSegment = pathPrefix ? normalizedPath.replace(`${pathPrefix}.`, '') : normalizedPath;

    if (isUrlLike(lastSegment)) return lastSegment;

    return toTitleCase(lastSegment ?? normalizedPath);
};

const isUrlLike = (value: string) => /^https?:\/\//i.test(value);

const isAddressLike = (value: unknown): value is { text?: string; city?: string; country_code?: string } =>
    isPlainObject(value) && ('text' in value || 'city' in value || 'country_code' in value);

const formatValuePreview = (value: unknown, useBadge = false): React.ReactNode => {
    if (value === undefined) return useBadge ? <span className='badge badge-light-dark'>{emDash}</span> : emDash;
    if (value === null) return useBadge ? <span className='badge badge-light-dark'>{emDash}</span> : 'null';
    if (typeof value === 'string') return useBadge ? <span className='badge badge-light-dark'>{value}</span> : value;
    if (typeof value === 'number' || typeof value === 'boolean') return useBadge ? <span className='badge badge-light-dark'>{value}</span> : String(value);
    if (Array.isArray(value)) {
        if (value.length === 0) return useBadge ? <span className='badge badge-light-dark'>{emDash}</span> : emDash;
        if (value.length > 3) {
            return useBadge ? <span className='badge badge-light-dark'>{`${value.length} items`}</span> : `${value.length} items`;
        }
        return <div className={useBadge ? 'd-flex flex-wrap gap-1' : ''}>
            {value.map((item, index) => <div key={index}>{renderValueBadges(item, useBadge)}</div>)}
        </div>;
    }
    if (isPlainObject(value)) {
        const keys = Object.keys(value);
        if (keys.length === 0) return useBadge ? <span className='badge badge-light-dark'>{emDash}</span> : emDash;
        if (keys.length > 3) {
            return useBadge ? <span className='badge badge-light-dark'>{`${keys.length} fields`}</span> : `${keys.length} fields`;
        }
        return <div className={useBadge ? 'd-flex flex-wrap gap-1' : ''}>
            {keys.map((key) => <div key={key}>{renderValueBadges(value[key], useBadge)}</div>)}
        </div>;
    }

    try {
        return useBadge ? <span className='badge badge-light-dark'>{String(value)}</span> : String(value);
    } catch {
        return useBadge ? <span className='badge badge-light-dark'>{emDash}</span> : emDash;
    }
};

const renderValueText = (value: unknown, useBadge = false) => (
    <span className='text-gray-700 text-wrap'>{formatValuePreview(value, useBadge)}</span>
);

const renderPolicyValue = (value: unknown, useBadge = true) => {
    if (typeof value !== 'string') {
        return useBadge ? (
            renderValueBadges(value, useBadge)
        ) : (
            renderValueText(value)
        );
    }

    return useBadge ? (
        <span className='badge badge-light-dark'>{value}</span>
    ) : (
        <span className='text-gray-700'>{value}</span>
    )
};

const renderAddressValue = (value: { text?: string; city?: string; country_code?: string }, key: string) => (
    <div key={key} className='border border-gray-200 rounded px-3 py-2 bg-light-dark'>
        {value.text && <div className='text-gray-800 text-wrap'>{value.text}</div>}
        {(value.city || value.country_code) && (
            <div className='text-muted fs-8'>
                {[value.city, value.country_code].filter(Boolean).join(', ')}
            </div>
        )}
    </div>
);

const renderObjectValue = (value: Record<string, unknown>, useBadge = true) => {
    const entries = Object.entries(value);

    if (entries.length === 0) return <span>{emDash}</span>;

    return (
        <div className='d-flex flex-column gap-1'>
            {entries.map(([key, itemValue]) => (
                isUrlLike(key) ? (
                    <div key={key} className='border border-gray-200 rounded px-3 py-2'>
                        <div className='text-break text-gray-800 fw-semibold'>{key}</div>
                        <div className='mt-2'>
                            {renderPolicyValue(itemValue, useBadge)}
                        </div>
                    </div>
                ) : (
                    <div key={key}>
                        {useBadge ? (
                            <span className='badge badge-light-dark'>{formatLeafLabel(key)}</span>
                        ) : (
                            <span className='text-gray-700 fw-semibold'>{formatLeafLabel(key)}</span>
                        )}
                        <div className='d-flex flex-wrap gap-1 align-items-center'>
                            {renderValueText(itemValue, !useBadge)}
                        </div>
                    </div>
                )
            ))}
        </div>
    );
};

const renderValueBadges = (value: unknown, useBadge = true): React.ReactNode => {
    if (value === undefined) return <span>{emDash}</span>;

    if (Array.isArray(value)) {
        if (value.length === 0) return <span>{emDash}</span>;

        if (value.every(isPrimitive)) {
            return (
                <div className={useBadge ? 'd-flex flex-wrap gap-1' : 'd-flex flex-column gap-1'}>
                    {value.map((item, index) => (
                        useBadge ? (
                            <span key={`${String(item)}-${index}`} className='badge badge-light-dark'>
                                {formatValuePreview(item)}
                            </span>
                        ) : (
                            <span key={`${String(item)}-${index}`} className='text-gray-700'>
                                {formatValuePreview(item)}
                            </span>
                        )
                    ))}
                </div>
            );
        }

        if (value.every(isAddressLike)) {
            return (
                <div className='d-flex flex-column gap-1'>
                    {value.map((item, index) => renderAddressValue(item, `address-${index}`))}
                </div>
            );
        }

        return (
            <div className='d-flex flex-column gap-1'>
                {value.map((item, index) => (
                    isPlainObject(item)
                        ? <div key={`object-${index}`}>{renderObjectValue(item, useBadge)}</div>
                        : (
                            useBadge ? (
                                <span key={`${String(item)}-${index}`} className='badge badge-light-dark'>
                                    {formatValuePreview(item)}
                                </span>
                            ) : (
                                <span key={`${String(item)}-${index}`} className='text-gray-700'>
                                    {formatValuePreview(item)}
                                </span>
                            )
                        )
                ))}
            </div>
        );
    }

    if (isPlainObject(value)) {
        if (isAddressLike(value)) {
            return renderAddressValue(value, 'address-single');
        }

        return renderObjectValue(value, useBadge);
    }

    return useBadge ? (
        <span className='badge badge-light-dark'>{formatValuePreview(value)}</span>
    ) : (
        renderValueText(value)
    );
};

const renderDiffValue = (value: unknown, type: DiffItem['type']) => {
    if (value === undefined) return <span className='text-gray-700'>{emDash}</span>;

    if (isPrimitive(value)) {
        return <span className='text-gray-700'>{formatValuePreview(value)}</span>;
    }

    return (
        <div style={type === 'changed' ? { width: '300px' } : { width: '600px' }}>
            {renderValueBadges(removeIdOrReturnRaw(value))}
        </div>
    );
};

const getPrimitiveArrayDiff = (
    previous: Primitive[],
    current: Primitive[],
    path: string
): DiffItem[] => {
    const previousCounts = new Map<string, { value: Primitive; count: number }>();
    const currentCounts = new Map<string, { value: Primitive; count: number }>();

    previous.forEach((item) => {
        const key = JSON.stringify(item);
        const existing = previousCounts.get(key);
        previousCounts.set(key, { value: item, count: (existing?.count ?? 0) + 1 });
    });

    current.forEach((item) => {
        const key = JSON.stringify(item);
        const existing = currentCounts.get(key);
        currentCounts.set(key, { value: item, count: (existing?.count ?? 0) + 1 });
    });

    const diffs: DiffItem[] = [];
    const keys = new Set([...Array.from(previousCounts.keys()), ...Array.from(currentCounts.keys())]);

    keys.forEach((key) => {
        const previousItem = previousCounts.get(key);
        const currentItem = currentCounts.get(key);
        const previousCount = previousItem?.count ?? 0;
        const currentCount = currentItem?.count ?? 0;

        if (previousCount > currentCount) {
            for (let index = 0; index < previousCount - currentCount; index += 1) {
                diffs.push({
                    type: 'removed',
                    path,
                    previousValue: previousItem?.value,
                });
            }
        }

        if (currentCount > previousCount) {
            for (let index = 0; index < currentCount - previousCount; index += 1) {
                diffs.push({
                    type: 'added',
                    path,
                    currentValue: currentItem?.value,
                });
            }
        }
    });

    if (
        diffs.length === 0 &&
        previous.length === current.length &&
        JSON.stringify(previous) !== JSON.stringify(current)
    ) {
        diffs.push({
            type: 'changed',
            path,
            previousValue: previous,
            currentValue: current,
        });
    }

    return diffs;
};

const getObjectWithIdArrayDiff = (
    previous: any[],
    current: any[],
    path: string
): DiffItem[] => {
    const diffs: DiffItem[] = [];

    // 1. Collect all _id that have been encountered (union)
    const allIds = new Set<string>();
    previous.forEach((item) => { if (isObjectWithId(item)) allIds.add(item._id); });
    current.forEach((item) => { if (isObjectWithId(item)) allIds.add(item._id); });

    // 2. Compare each _id
    allIds.forEach((id) => {
        const prevItem = previous.find((item) => isObjectWithId(item) && item._id === id);
        const currItem = current.find((item) => isObjectWithId(item) && item._id === id);
        const itemPath = buildPath(path, id);

        // If both exist and content is different, mark as changed
        if (prevItem && currItem && JSON.stringify(prevItem) !== JSON.stringify(currItem)) {
            diffs.push({
                type: 'changed',
                path: itemPath,
                previousValue: prevItem,
                currentValue: currItem
            });
            return;
        }

        // Handle the case of addition or deletion, and call diffValues recursively
        diffs.push(...diffValues(prevItem, currItem, itemPath));
    });

    return diffs;
};

const diffValues = (previous: unknown, current: unknown, path = 'value'): DiffItem[] => {
    if (JSON.stringify(previous) === JSON.stringify(current)) {
        return [];
    }

    if (previous === undefined) {
        return [{ type: 'added', path, currentValue: current }];
    }

    if (current === undefined) {
        return [{ type: 'removed', path, previousValue: previous }];
    }

    if (Array.isArray(previous) && Array.isArray(current)) {
        const allPrimitive = previous.every(isPrimitive) && current.every(isPrimitive);
        if (allPrimitive) {
            return getPrimitiveArrayDiff(previous as Primitive[], current as Primitive[], path);
        }

        const allObjectWithId = previous.every(isObjectWithId) && current.every(isObjectWithId);
        if (allObjectWithId) {
            return getObjectWithIdArrayDiff(previous, current, path);
        }

        const diffs: DiffItem[] = [];
        const maxLength = Math.max(previous.length, current.length);

        for (let index = 0; index < maxLength; index += 1) {
            diffs.push(...diffValues(previous[index], current[index], `${path}[${index}]`));
        }

        return diffs;
    }

    if (isPlainObject(previous) && isPlainObject(current)) {
        const diffs: DiffItem[] = [];
        const keys = new Set([...Object.keys(previous), ...Object.keys(current)]);

        keys.forEach((key) => {
            diffs.push(
                ...diffValues(
                    previous[key],
                    current[key],
                    buildPath(path, key)
                )
            );
        });

        return diffs;
    }

    return [
        {
            type: 'changed',
            path,
            previousValue: previous,
            currentValue: current,
        },
    ];
};

const getBadgeClassName = (type: DiffItem['type']) => {
    switch (type) {
        case 'added':
            return 'badge badge-light-success';
        case 'removed':
            return 'badge badge-light-danger';
        case 'changed':
        default:
            return 'badge badge-light-primary';
    }
};

const getIndexedPathSegment = (fieldPath: string, diffPath: string) => {
    if (!fieldPath || !diffPath.startsWith(fieldPath)) return null;

    const relativePath = diffPath.slice(fieldPath.length);
    return relativePath.match(/^\[(\d+)\]/);
};

const getBrandNameForDiff = (fieldPath: string, diff: DiffItem, value: unknown) => {
    if (fieldPath !== 'result.brands' || !Array.isArray(value)) return undefined;

    const pathMatch = getIndexedPathSegment(fieldPath, diff.path);
    const index = pathMatch ? Number(pathMatch[1]) : NaN;

    if (Number.isNaN(index)) return undefined;

    const currentItem = value[index];
    if (!isPlainObject(currentItem) || typeof currentItem.brand !== 'string') return undefined;

    return currentItem.brand;
};

const getItemLabelFromArrayValue = (fieldPath: string, diff: DiffItem, value: unknown) => {
    if (!Array.isArray(value)) return undefined;

    const pathMatch = getIndexedPathSegment(fieldPath, diff.path);
    const index = pathMatch ? Number(pathMatch[1]) : NaN;

    if (Number.isNaN(index)) return undefined;

    const currentItem = value[index];
    if (!isPlainObject(currentItem)) return undefined;
    if (typeof currentItem.brand === 'string') return currentItem.brand;
    if (typeof currentItem.category === 'string') return currentItem.category;
    if (typeof currentItem.name === 'string') return currentItem.name;

    return undefined;
};

const getBrandNameFromValue = (value: unknown) => {
    if (!isPlainObject(value) || typeof value.brand !== 'string') return undefined;

    return value.brand;
};

const getCategoryNameFromValue = (value: unknown) => {
    if (!isPlainObject(value) || typeof value.category !== 'string') return undefined;

    return value.category;
};

const getDiffTypeLabel = (type: DiffItem['type']) => {
    switch (type) {
        case 'added':
            return 'Added';
        case 'removed':
            return 'Removed';
        case 'changed':
        default:
            return 'Changed';
    }
};

const renderDiffHeading = (fieldPath: string, diff: DiffItem, value: unknown) => {
    const itemLabel =
        getBrandNameForDiff(fieldPath, diff, value) ??
        getItemLabelFromArrayValue(fieldPath, diff, value) ??
        getBrandNameFromValue(diff.currentValue) ??
        getBrandNameFromValue(diff.previousValue) ??
        getCategoryNameFromValue(diff.currentValue) ??
        getCategoryNameFromValue(diff.previousValue);
    const isRootArrayItemChange =
        diff.type !== 'changed' &&
        diff.path.replace(/\[\d+\]$/, '') === fieldPath &&
        /^result\.[^.]+\[\d+\]$/.test(diff.path);

    if (itemLabel && /^result\.[^.]+\[\d+\]$/.test(diff.path)) {
        return (
            <div className='d-flex flex-column gap-1 align-items-start'>
                <span className={`${getBadgeClassName(diff.type)} align-self-start`}>
                    {getDiffTypeLabel(diff.type)}
                </span>
                <span className='badge badge-light-dark align-self-start'>{itemLabel}</span>
            </div>
        );
    }

    if (isRootArrayItemChange) {
        return (
            <span className={`${getBadgeClassName(diff.type)} align-self-start`}>
                {getDiffTypeLabel(diff.type)}
            </span>
        );
    }

    if (!itemLabel || !diff.path.endsWith('.field_status')) {
        return (
            <span className={`${getBadgeClassName(diff.type)} align-self-start`}>
                {formatLeafLabel(diff.path, fieldPath)}
            </span>
        );
    }

    return (
        <div className='d-flex flex-wrap gap-2 align-items-center'>
            <span className='badge badge-light-dark align-self-start'>{itemLabel}</span>
            <span className={`${getBadgeClassName(diff.type)} align-self-start`}>
                {formatLeafLabel(diff.path, fieldPath)}
            </span>
        </div>
    );
};

const getDisplayDiffValue = (fieldPath: string, diff: DiffItem) => {
    const sourceValue = diff.type === 'removed' ? diff.previousValue : diff.currentValue;

    if (
        fieldPath === 'result.brands' &&
        /^result\.brands\[\d+\]$/.test(diff.path) &&
        isPlainObject(sourceValue)
    ) {
        const { brand, ...rest } = sourceValue;
        return Object.keys(rest).length > 0 ? rest : undefined;
    }

    return sourceValue;
};

const isInitialHistoryEntry = (
    fieldPath: string,
    diffs: DiffItem[],
    updatedBy?: string | null
) => (
    diffs.length === 1 &&
    diffs[0].type === 'added' &&
    diffs[0].path === fieldPath &&
    !updatedBy
);

const getValueFromValueMap = (value: unknown, valueMap?: Map<unknown, unknown>) => {
    if (isPrimitive(value)) return valueMap?.get(value) ? `${value} (${valueMap.get(value)})` : value;
    if (Array.isArray(value)) return value.map((v) => getValueFromValueMap(v, valueMap)) as unknown[];
    if (isPlainObject(value)) return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, getValueFromValueMap(v, valueMap)])) as Record<string, unknown>;
    return value;
};

const summarizeDiffs = (
    fieldPath: string,
    diffs: DiffItem[],
    value: unknown,
    updatedBy?: string | null,
) => {
    if (!diffs.length) {
        return <span className='text-muted'>No detectable changes.</span>;
    }

    const isInitialValue = isInitialHistoryEntry(fieldPath, diffs, updatedBy);

    if (isInitialValue) {
        return (
            <div className='d-flex flex-column gap-1'>
                <span className='badge badge-dark align-self-start'>Initial value</span>
                {renderValueBadges(value)}
            </div>
        );
    }

    const groupedPrimitiveChanges = new Map<
        string,
        { added: unknown[]; removed: unknown[] }
    >();
    const changedItems: DiffItem[] = [];

    diffs.forEach((diff) => {
        const isRootLevelCollectionChange = diff.path === fieldPath;

        if (isRootLevelCollectionChange && (diff.type === 'added' || diff.type === 'removed')) {
            const existing = groupedPrimitiveChanges.get(diff.path) ?? { added: [], removed: [] };

            if (diff.type === 'added') {
                existing.added.push(diff.currentValue);
            }

            if (diff.type === 'removed') {
                existing.removed.push(diff.previousValue);
            }

            groupedPrimitiveChanges.set(diff.path, existing);
            return;
        }

        changedItems.push(diff);
    });

    return (
        <div className='d-flex flex-column gap-1'>
            {Array.from(groupedPrimitiveChanges.entries()).map(([path, changeGroup]) => (
                <div key={path} className='d-flex flex-column gap-1'>
                    {path !== fieldPath && <span className='badge badge-light-dark align-self-start'>{formatLeafLabel(path, fieldPath)}</span>}
                    {changeGroup.added.length > 0 && (
                        <div className='d-flex flex-column gap-1'>
                            <span className={`${getBadgeClassName('added')} align-self-start`}>Added</span>
                            <div className='d-flex flex-column gap-1'>
                                {changeGroup.added.map((item, index) => (
                                    <div key={`${path}-added-${index}`}>
                                        {renderDiffValue(item, 'added')}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {changeGroup.removed.length > 0 && (
                        <div className='d-flex flex-column gap-1'>
                            <span className={`${getBadgeClassName('removed')} align-self-start`}>Removed</span>
                            <div className='d-flex flex-column gap-1'>
                                {changeGroup.removed.map((item, index) => (
                                    <div key={`${path}-removed-${index}`}>
                                        {renderDiffValue(item, 'removed')}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            ))}

            {changedItems.map((diff, index) => (
                <div key={`${diff.path}-${diff.type}-${index}`} className='d-flex flex-column gap-1'>
                    {renderDiffHeading(fieldPath, diff, value)}
                    {diff.type === 'changed' ? (
                        <div className='d-flex align-items-start gap-3 flex-wrap flex-sm-nowrap'>
                            {renderDiffValue(diff.previousValue, 'changed')}
                            <span className='text-gray-500 fw-semibold align-self-center'>-&gt;</span>
                            {renderDiffValue(diff.currentValue, 'changed')}
                        </div>
                    ) : diff.type === 'added' ? (
                        renderDiffValue(getDisplayDiffValue(fieldPath, diff), 'added')
                    ) : (
                        renderDiffValue(getDisplayDiffValue(fieldPath, diff), 'removed')
                    )}
                </div>
            ))}
        </div>
    );
};

const getGroupedHistory = (responses?: ContextualHistoryResponse[], valueMap?: Map<unknown, unknown>): GroupedHistoryItem[] => {
    return (responses ?? [])
        .flatMap((response) => Object.entries(response?.results ?? {}).map(([path, entries]) => ({
            key: `${response.ref_id}:${path}`,
            path,
            fieldLabel: response.pathLabels?.[path] ??
                (response.label ? `${response.label} / ${formatPathLabel(path)}` : formatPathLabel(path)),
            entries: valueMap
                ? entries.map((entry) => ({
                    ...entry,
                    value: getValueFromValueMap(entry.value, valueMap),
                }))
                : entries,
        })))
        .map(({ key, path, fieldLabel, entries }) => {
            const sortedEntries = [...(entries ?? [])].sort(
                (a, b) => (b.version ?? 0) - (a.version ?? 0)
            );

            const entriesWithDiff = sortedEntries.map((entry, index) => {
                const previousEntry = sortedEntries[index + 1];

                return {
                    ...entry,
                    path,
                    diffs: previousEntry
                        ? diffValues(previousEntry.value, entry.value, path)
                        : [
                            {
                                type: 'added' as const,
                                path,
                                currentValue: entry.value,
                            },
                        ],
                };
            }).filter((entry) => !isInitialHistoryEntry(path, entry.diffs, entry.updated_by));

            return {
                key,
                path,
                fieldLabel,
                entries: entriesWithDiff,
            };
        })
        .filter((item) => item.entries.length > 0);
};

type Props = {
    show: boolean;
    handleClose: () => void;
    data?: any;
    service_paths: ServicePath[];
    historyReference?: HistoryReference;
    summaryValueMap?: Map<unknown, unknown>;
};

const HistoryModal: React.FC<Props> = ({
    show,
    handleClose,
    data,
    service_paths,
    historyReference = 'SITE',
    summaryValueMap,
}) => {
    const { currentUser, auth, refresh } = useAuth();
    const intl = useIntl();
    const { setToast } = useToast();
    const reviewerNames: Reviewer[] = data?.results?.users ?? [];
    const validServicePaths = (service_paths ?? []).filter(
        (servicePath) => !!servicePath?.ref_id && servicePath?.paths?.length > 0
    );

    const getReviewerName = (userId?: string | null) => {
        if (!userId) return emDash;

        const matchedReviewer = reviewerNames.find((reviewer) => reviewer.user_id === userId);
        return matchedReviewer?.email ?? matchedReviewer?.name ?? userId;
    };

    const siteHistoryQuery = useQuery(
        ['site-intelligence-field-updates', validServicePaths],
        async () => {
            const responses = await Promise.all(
                validServicePaths.map(async ({ ref_id, paths, label, pathLabels }) => {
                    try {
                        const response = await api(
                            `admin/v1/list_site_intelligence_field_updates`,
                            {
                                method: 'POST',
                                headers: {
                                    'content-type': 'application/json',
                                },
                                body: JSON.stringify({
                                    ref_id,
                                    paths,
                                }),
                            },
                            { token: auth?.accessToken, refresh },
                            'site-intelligence',
                        );
                        const result = await response?.json();
                        const status = response?.status;

                        if (status === 200) {
                            return {
                                ...(result as HistoryResponse),
                                ref_id,
                                label,
                                pathLabels,
                            } as ContextualHistoryResponse;
                        }
                    } catch (err) {
                        const response = await err?.response?.json();
                        setToast({
                            show: true,
                            type: 'Danger',
                            header: intl.formatMessage({ id: 'MESSAGE.ERROR' }),
                            message: response?.message ?? err.message,
                        });
                    }

                    return {
                        ref_id,
                        label,
                        pathLabels,
                        results: {},
                    } as ContextualHistoryResponse;
                })
            );

            return responses;
        },
        {
            enabled: show && validServicePaths.length > 0 && historyReference === 'SITE',
            keepPreviousData: true,
        }
    );

    const siRequestHistoryQuery = useQuery(
        ['site-intelligence-request-field-updates', validServicePaths],
        async () => {
            const responses = await Promise.all(
                validServicePaths.map(async ({ ref_id, paths, label, pathLabels }) => {
                    try {
                        const response = await api(
                            `admin/v1/list_site_intelligence_request_field_updates`,
                            {
                                method: 'POST',
                                headers: {
                                    'content-type': 'application/json',
                                },
                                body: JSON.stringify({
                                    ref_id,
                                    paths,
                                }),
                            },
                            { token: auth?.accessToken, refresh },
                            'site-intelligence',
                        );
                        const result = await response?.json();
                        const status = response?.status;

                        if (status === 200) {
                            return {
                                ...(result as HistoryResponse),
                                ref_id,
                                label,
                                pathLabels,
                            } as ContextualHistoryResponse;
                        }
                    } catch (err) {
                        const response = await err?.response?.json();
                        setToast({
                            show: true,
                            type: 'Danger',
                            header: intl.formatMessage({ id: 'MESSAGE.ERROR' }),
                            message: response?.message ?? err.message,
                        });
                    }

                    return {
                        ref_id,
                        label,
                        pathLabels,
                        results: {},
                    } as ContextualHistoryResponse;
                })
            );

            return responses;
        },
        {
            enabled: show && validServicePaths.length > 0 && historyReference === 'SI_REQUEST',
            keepPreviousData: true,
        }
    );

    const getHistoryQuery = (historyReference: HistoryReference) => {
        switch (historyReference) {
            case 'SITE':
                return siteHistoryQuery;
            case 'SI_REQUEST':
                return siRequestHistoryQuery;
            default:
                return undefined;
        }
    };

    const historyQuery = getHistoryQuery(historyReference);

    const groupedHistory = getGroupedHistory(historyQuery?.data as ContextualHistoryResponse[], summaryValueMap);
    const tableData: HistoryTableRow[] = groupedHistory.flatMap((group) =>
        group.entries
            .map((entry) => ({
                field: group.fieldLabel,
                updatedBy: getReviewerName(entry.updated_by),
                updatedAt: entry.updated_at,
                summary: summarizeDiffs(group.path, entry.diffs, entry.value, entry.updated_by),
            }))
            .sort((a, b) => (b.updatedAt ? new Date(b.updatedAt).getTime() : 0) - (a.updatedAt ? new Date(a.updatedAt).getTime() : 0))
    );

    const columns: Column<HistoryTableRow>[] = [
        {
            Header: 'Field',
            accessor: (row) => <span className='fw-bold text-gray-800'>{row.field}</span>,
            minWidth: 180,
            id: 'field',
        },
        {
            Header: 'Updated By',
            accessor: (row) => row.updatedBy,
            minWidth: 175,
            id: 'updatedBy',
        },
        {
            Header: 'Updated At',
            accessor: (row) =>
                row.updatedAt ? (
                    <Time date={new Date(row.updatedAt)} format='yyyy-MM-dd HH:mm:ss' />
                ) : (
                    emDash
                ),
            minWidth: 175,
            id: 'updatedAt',
        },
        {
            Header: 'Summary',
            accessor: (row) => row.summary,
            minWidth: 420,
            id: 'summary',
        },
    ];

    return (
        <Modal
            id='history_modal'
            tabIndex={-1}
            show={show}
            onHide={handleClose}
            centered
            dialogClassName='modal-dialog-centered'
            style={{
                '--bs-modal-width': 'fit-content',
            } as React.CSSProperties}
        >
            <div className='container-xxl px-10 py-10'>
                <div className='modal-header py-2 d-flex border-0'>
                    <div>
                        <h1 className='d-flex align-items-center text-dark fw-bolder my-1 fs-3'>
                            Update History
                        </h1>
                    </div>
                    <div className='mr-auto btn btn-icon btn-sm btn-light-primary' onClick={handleClose}>
                        <KTSVG className='svg-icon-2' path='/media/icons/duotune/arrows/arr061.svg' />
                    </div>
                </div>

                <div className='modal-body pt-6'>
                    {historyQuery.isLoading ? (
                        <div className='d-flex justify-content-center align-items-center py-15'>
                            <div className='spinner-border text-primary' role='status'>
                                <span className='visually-hidden'>Loading...</span>
                            </div>
                        </div>
                    ) : tableData.length > 0 ? (
                        <div>
                            <SimpleDatatable
                                data={tableData}
                                columns={columns}
                                className='table-row-dashed mb-0'
                            />
                        </div>
                    ) : (
                        <div className='text-center text-muted py-10'>No update history found.</div>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export { HistoryModal };
